if (!customElements.get('speakon-agent-scenes')) {
  customElements.define('speakon-agent-scenes', class extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.resizeObserver?.disconnect();
      this.observer?.disconnect();
      clearTimeout(this.timer);
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.tabs = [...this.querySelectorAll('[data-scene-tab]')];
      this.panels = [...this.querySelectorAll('[data-scene-panel]')];
      this.frames = [...this.querySelectorAll('[data-scene-frame]')];
      this.previews = [...this.querySelectorAll('[data-scene-preview]')];
      this.navigation = this.querySelector('[role="tablist"]');
      this.indicator = this.querySelector('[data-scene-indicator]');
      this.desktop = matchMedia('(min-width: 1200px)');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.interval = Number(this.dataset.autoplayInterval) || 3000;
      this.inView = false;
      this.keyboardFocused = false;
      this.editorSelected = false;
      const resize = () => {
        this.setAttribute('data-instant', '');
        this.navigation?.setAttribute('aria-orientation', this.desktop.matches ? 'vertical' : 'horizontal');
        this.positionIndicator();
      };
      this.desktop.addEventListener('change', resize, options);
      this.addEventListener('click', event => {
        const button = event.target instanceof Element ? event.target.closest('[data-scene-select]') : null;
        if (!button || !this.contains(button)) return;
        if (event.detail > 0) this.keyboardFocused = false;
        // Move focus out of a preview before its outgoing panel becomes inert.
        this.select(Number(button.dataset.sceneSelect), { focus: button.hasAttribute('data-scene-preview'), animate: event.detail > 0 });
      }, options);
      this.navigation?.addEventListener('keydown', event => {
        const current = this.tabs.indexOf(event.target);
        if (current < 0) return;
        this.keyboardFocused = true;
        this.updatePlayback();
        if (['Enter', ' '].includes(event.key)) this.setAttribute('data-instant', '');
        let next;
        if (['ArrowRight', 'ArrowDown'].includes(event.key)) next = (current + 1) % this.tabs.length;
        if (['ArrowLeft', 'ArrowUp'].includes(event.key)) next = (current - 1 + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabs.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        this.select(next, { focus: true, animate: false });
      }, options);
      this.addEventListener('shopify:block:select', event => {
        const index = this.panels.findIndex(panel => panel.dataset.blockId === event.detail?.blockId);
        if (index >= 0) {
          this.editorSelected = true;
          this.select(index, { animate: false });
        }
      }, options);
      this.addEventListener('shopify:block:deselect', () => {
        this.editorSelected = false;
        this.updatePlayback();
      }, options);
      this.addEventListener('focusin', event => {
        this.keyboardFocused = event.target instanceof Element && event.target.matches(':focus-visible');
        this.updatePlayback();
      }, options);
      this.addEventListener('focusout', event => {
        if (this.contains(event.relatedTarget)) return;
        this.keyboardFocused = false;
        this.updatePlayback();
      }, options);
      this.motion.addEventListener('change', () => this.updatePlayback(), options);
      document.addEventListener('visibilitychange', () => this.updatePlayback(), options);
      this.select(0, { animate: false });
      resize();
      this.setAttribute('data-enhanced', '');
      if (this.navigation) {
        this.resizeObserver = new ResizeObserver(resize);
        this.resizeObserver.observe(this.navigation);
      }
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting && entries[0].intersectionRatio >= .15;
        this.updatePlayback();
      }, { threshold: [0, .15] });
      this.observer.observe(this.querySelector('.speakon-agent-scenes__stage') || this);
    }

    select(index, { focus = false, animate = true } = {}) {
      if (!this.tabs[index] || !this.panels[index]) return;
      this.toggleAttribute('data-instant', !animate || this.motion.matches);
      this.index = index;
      if (focus) this.tabs[index].focus({ preventScroll: true });
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
      });
      this.panels.forEach((panel, i) => {
        panel.toggleAttribute('data-active', i === index);
        panel.setAttribute('aria-hidden', String(i !== index));
        panel.toggleAttribute('inert', i !== index);
        this.frames[i].toggleAttribute('data-active', i === index);
        const depth = (i - index + this.tabs.length) % this.tabs.length;
        // Recycle outgoing cards behind the stack immediately; only advancing cards animate forward.
        this.frames[i].toggleAttribute('data-recycled', depth > Number(this.frames[i].dataset.depth));
        this.frames[i].dataset.depth = depth;
        this.previews[i].disabled = i === index;
        this.previews[i].setAttribute('aria-hidden', String(i === index));
        this.previews[i].tabIndex = i === index ? -1 : 0;
      });
      this.positionIndicator();
      this.updatePlayback();
    }

    updatePlayback() {
      clearTimeout(this.timer);
      if (!this.autoplayEnabled || !this.isConnected || !this.inView || this.motion.matches || document.hidden || this.keyboardFocused || this.editorSelected || this.tabs.length < 2) return;
      this.timer = setTimeout(() => this.select((this.index + 1) % this.tabs.length), this.interval);
    }

    positionIndicator() {
      if (!this.indicator || !this.navigation || !this.tabs[this.index]) return;
      const nav = this.navigation.getBoundingClientRect();
      const tab = this.tabs[this.index].getBoundingClientRect();
      if (!nav.width || !nav.height) return;
      const vertical = this.desktop.matches;
      const x = tab.left - nav.left;
      const y = vertical ? tab.top - nav.top : tab.bottom - nav.top - 3;
      const width = vertical ? 4 : tab.width;
      const height = vertical ? tab.height : 3;
      this.indicator.style.transform = `translate(${x}px, ${y}px) scale(${width / nav.width}, ${height / nav.height})`;
    }

    disconnectedCallback() {
      this.events?.abort();
      this.resizeObserver?.disconnect();
      this.observer?.disconnect();
      clearTimeout(this.timer);
    }
  });
}
